import { getBaseURL } from "../../web/src/js/baseConfig";
import {Image, Layer, Tile, VectorTile as VectorTileLayer} from "ol/layer";
import { ImageWMS, TileWMS, XYZ, WMTS, BingMaps, OSM, VectorTile } from "ol/source";
import {optionsFromCapabilities} from 'ol/source/WMTS';
import WMTSCapabilities from 'ol/format/WMTSCapabilities';
import MVT from 'ol/format/MVT';
import { silentapifetch } from "vueforms/src/utils/apifetch"
import GeoJSON from 'ol/format/GeoJSON';
import VectorSource from 'ol/source/Vector';
import {Vector as VectorLayer} from 'ol/layer';
import {bbox as bboxStrategy} from 'ol/loadingstrategy';
import KMZ from "../../web/src/js/KMZFormat";
import {KML} from 'ol/format';
import globals from "../../web/src/js/globals";
import { notifications } from "mapview-gui-utils/src/notification/Notifications";
import ko from "knockout";
// import { apply } from "ol-mapbox-style"
import { MapLibreLayer } from "@geoblocks/ol-maplibre-layer";
import LayerGroup from "ol/layer/Group";
import { BKGWebserviceAuthentication } from "./WebServiceAuthentication";

let DEFAULT_TILECACHE_SIZE = 200;

interface AuthenticationConfig {
  type: "BKG" | "none";
  APIKey?: string;
}

const zValues = {
  BELOW_WATER: 0,
  ABOVE_WATER: 1,
  MASK: 2,
};

export interface BackgroundLayerEntry {
  type: "WMS" | "TileWMS" | "XYZ" | "WMTS" | "Satellite" | "Bing" | "OSM" | "WFS" | "KMZ" | "KML" | "blank" | "VectorTile";
  label?: string;
  icon?: string;
  /**
   * Nur fuer AdditionalLayer in Variantenmodellen relevant:
   * Enthaelt die Positionen der Einzelmodelle eines Variantenmodells, fuer die der Layer sichtbar sein soll.
   */
  visibleForModelsWithPosition?: number[];
  /**
   * Nur fuer AdditionalLayer in Variantenmodellen relevant: Steuert die initiale Sichtbarkeit.
   */
  initialVisibility?: boolean;

  url: string;
  properties: {
    GetMapURL?: string; // WMS
    layer?: string;     // WMTS
    attributions?: string;
    layerOpts?: object;
    belowWater?: boolean;
    zValue?: typeof zValues;
    authentication?: AuthenticationConfig;
  };
  layerProperties: {
    visible: boolean;
    opacity: number;
  };
  canToggle: boolean;
  zValue: typeof zValues;
}

class BackgroundLayerFactory{
  static async createBackgroundLayer(backgroundLayerEntry: BackgroundLayerEntry, isDefault: boolean, id: number){
    let backgroundLayer;
    switch(backgroundLayerEntry.type){
      case "WMS":
        backgroundLayer = new BackgroundLayerWMS(backgroundLayerEntry, isDefault, id);
        break;
      case "TileWMS":
        backgroundLayer = new BackgroundLayerTileWMS(backgroundLayerEntry, isDefault, id);
        break;
      case "XYZ":
        backgroundLayer = new BackgroundLayerXYZ(backgroundLayerEntry, isDefault, id);
        break;
      case "WMTS":
        backgroundLayer = new BackgroundLayerWMTS(backgroundLayerEntry, isDefault, id);
        break;
      case "Satellite":
        backgroundLayer = new BackgroundLayerESRIBasemapVectorTile(backgroundLayerEntry, isDefault, id);
        break;
      case "Bing":
        backgroundLayer = new BackgroundLayerESRIBasemapVectorTile(backgroundLayerEntry, isDefault, id);
        break;
      case "OSM":
        backgroundLayer = new BackgroundLayerOSM(backgroundLayerEntry, isDefault, id);
        break;
      case "blank":
        backgroundLayer = new BackgroundLayerBlank(backgroundLayerEntry, isDefault, id);
        break;
      case "WFS":
        backgroundLayer = new BackgroundLayerWFS(backgroundLayerEntry, isDefault, id);
        break;
      case "KMZ":
        backgroundLayer = new BackgroundLayerKMZ(backgroundLayerEntry, isDefault, id);
        break;
      case "KML":
        backgroundLayer = new BackgroundLayerKMZ(backgroundLayerEntry, isDefault, id);
        break;
      case "VectorTile":
        backgroundLayer = new BackgroundLayerVectorTile(backgroundLayerEntry, isDefault, id);
        break;
    }
    if(!backgroundLayer){
      return undefined;
    }
    await backgroundLayer.init();
    return backgroundLayer;
  }
}

function relativizeUrl(url: string) {
  let regex = /https?:\/\//;
  if (!regex.test(url)) {
    return getBaseURL() + url;
  }
  return url;
}

class BackgroundLayer {
  id: number;
  isDefault: boolean;
  type: string;
  label: string;
  icon: string;
  visibleForModelsWithPosition?: number[];
  initialVisibility: boolean | undefined;
  url: string;
  properties: any;
  layerProperties: any;
  visible: ko.Observable<boolean>;
  canToggle: boolean;
  zValue: number;
  layer: Layer | undefined;
  autehnticationConfig: AuthenticationConfig | undefined;

  constructor( backgroundLayerEntry: BackgroundLayerEntry, isDefault: boolean, id: number){
    this.id = id;
    this.isDefault = isDefault;
    this.type = backgroundLayerEntry.type;
    this.label = backgroundLayerEntry.label;
    this.icon = backgroundLayerEntry.icon;
    this.visibleForModelsWithPosition = backgroundLayerEntry.visibleForModelsWithPosition;
    this.initialVisibility = true;
    if (Array.isArray(backgroundLayerEntry.visibleForModelsWithPosition) &&
        !backgroundLayerEntry.visibleForModelsWithPosition.includes(1)) {
      this.initialVisibility = false;
    }

    /** @type {string} */
    this.url = backgroundLayerEntry?.properties?.GetMapURL ?? (backgroundLayerEntry.url || "");
    this.autehnticationConfig = backgroundLayerEntry?.properties?.authentication;
    if(this.autehnticationConfig){
      delete backgroundLayerEntry.properties.authentication;
    }
    this.url = this.url.trim();
    this.url = relativizeUrl(this.url);
    this.properties = backgroundLayerEntry.properties || {};
    this.layerProperties = backgroundLayerEntry.layerProperties;
    if (this.layerProperties?.visible && isDefault === false) {
      this.layerProperties.visible = false;
    }
    this.properties.transition = 0;
    this.visible = ko.observable(false);
    this.canToggle = backgroundLayerEntry?.canToggle;
    this.zValue = backgroundLayerEntry.zValue ?? backgroundLayerEntry.properties?.zValue;
  }

  async authenticate(){
    if(this.autehnticationConfig){
      if(this.autehnticationConfig.type === "BKG"){
        let bkgAuth = new BKGWebserviceAuthentication(this.autehnticationConfig.APIKey ?? BKG_API_KEY);
        await bkgAuth.authorize();
        this.url = bkgAuth.getServiceURL(this.url);
      }
    }
  }

  async init() {
    if (!this.icon) {
      this.icon = "map";
    }
    
    await this.authenticate();
    this.layer = await this.getLayerDefinition();

    if(!this.layer){
      throw "Hintergrundkarte konnte nicht initialisiert werden.";
    }
    
    this.visible(this.layer.getVisible());
    this.layer.on("change:visible", (e) => {
      if (this.visible() != this.layer?.getVisible()) {
        this.visible(this.layer?.getVisible() ?? false);
      }
    });
    this.visible.subscribe(() => {
      if (this.visible() != this.layer?.getVisible()) {
        this.layer?.setVisible(this.visible());
      }
    });
  }

  async getLayerDefinition(): Promise<Layer | undefined> {
    throw "abstract";
  }

  getIcon(){
    return this.icon;
  }

  showLayer(){
    this.layer?.setVisible(true);
  }

  hideLayer(){
    this.layer?.setVisible(false);
  }
}

class BackgroundLayerWMS extends BackgroundLayer {
  async getLayerDefinition(){
    return new Image({
      source: new ImageWMS({
        url: this.url.replace(/\?(.*)/,""),
        ...this.properties
      }),
      ...this.layerProperties,
      visible: this.isDefault && this.initialVisibility && this.layerProperties?.visible
    });
  }
}

class BackgroundLayerTileWMS extends BackgroundLayer {
  async getLayerDefinition(){
    return new Tile({
      source: new TileWMS({
        url: this.url.replace(/\?(.*)/, ""),
        ...this.properties
      }),
      ...this.layerProperties,
      visible: this.isDefault && this.initialVisibility && this.layerProperties?.visible
    });
  }
}

class BackgroundLayerXYZ extends BackgroundLayer {
  async getLayerDefinition(){
    return new Tile({
      // preload: Infinity,
      source: new XYZ({
        url: this.url,
        cacheSize: DEFAULT_TILECACHE_SIZE,
        ...this.properties
      }),
      ...this.layerProperties,
      visible: this.isDefault && this.initialVisibility && this.layerProperties?.visible
    });
  }
}

class BackgroundLayerWMTS extends BackgroundLayer {
  async getLayerDefinition() {
    let parser = new WMTSCapabilities();
    try {
      let result
      try {
        result = await silentapifetch(this.url).then(e => e.text());
      } catch (e) {
        if (["/", "?"].indexOf(this.url[this.url.length - 1]) !== -1) {
          this.url = this.url.slice(0, this.url.length - 1);
        }
        try {
          result = await silentapifetch(this.url + "/1.0.0/WMTSCapabilities.xml").then(e => e.text());
        }
        catch (e2) {
          try {
            result = await silentapifetch(this.url + "?SERVICE=WMTS&REQUEST=GetCapabilities").then(e => e.text());
          }
          catch(e3){
            notifications.addMessage(`WMTS-Layer ${this.label} konnte nicht geladen werden.`)
            throw e3;
          }
        }
      }
      let parsedCapabilities = parser.read(result);
      let options = optionsFromCapabilities(parsedCapabilities, {
        layer: this.properties.layer,
        projection: 'EPSG:3857',
      });
      options.transition = 0;
      options.attributions = this.properties.attributions;
      options.maxZoom = 17;
      return new Tile({
        cacheSize: DEFAULT_TILECACHE_SIZE,
        source: new WMTS(options),
        ...this.layerProperties,
        visible: this.isDefault && this.initialVisibility && this.layerProperties?.visible
      });
    }
    catch (e) {
      console.error(e);
      notifications.addMessage(`Hintergrundkarte <b>${this.label}</b>: Fehler in der Definition`);
    }
    return undefined;
  }

}

class BackgroundLayerESRIBasemapVectorTile extends BackgroundLayer{

  API_KEY = __ESRI_API_KEY__;


  constructor(backgroundLayerEntry: BackgroundLayerEntry, isDefault: boolean, id: number){
    super(backgroundLayerEntry, isDefault, id);
    this.label = this.label || "Satellit";
    this.icon = this.icon || "mountain";
  }

  async getLayerDefinition(): Promise<Layer | undefined> {
    const basemapId = "arcgis/imagery";

    const layer = new MapLibreLayer({
      mapLibreOptions: {
        style: `https://basemapstyles-api.arcgis.com/arcgis/rest/services/styles/v2/styles/${basemapId}?type=style&token=${this.API_KEY}`,
      },
      ...this.layerProperties,
      visible: this.isDefault && this.initialVisibility && this.layerProperties?.visible
    });
    
    return layer
  }
}

class BackgroundLayerVectorTile extends BackgroundLayer {
  async getLayerDefinition(): Promise<Layer | undefined> {
    const layer = new MapLibreLayer({
      mapLibreOptions: {
        style: this.url,
      },
      ...this.layerProperties,
      visible: this.isDefault && this.initialVisibility && this.layerProperties?.visible
    });
    return layer;
  }
}

class BackgroundLayerOSM extends BackgroundLayer {
  constructor(backgroundLayerEntry: BackgroundLayerEntry, isDefault: boolean, id: number){
    super(backgroundLayerEntry, isDefault, id);
    this.label = this.label || "Karte";
    this.icon = this.icon || "map";
  }

  async getLayerDefinition(){
    return new Tile({
      // preload: Infinity,
      source: new OSM({
        transition: 0,
        cacheSize: DEFAULT_TILECACHE_SIZE,
      }),
      ...this.layerProperties,
      visible: this.isDefault && this.initialVisibility && this.layerProperties?.visible
    });
  }
}

class BackgroundLayerBlank extends BackgroundLayer {
  constructor(backgroundLayerEntry: BackgroundLayerEntry, isDefault: boolean, id: number){
    super(backgroundLayerEntry, isDefault, id);
    this.label = this.label || "Karte";
    this.icon = this.icon || "map";
  }

  async getLayerDefinition(){
    return new Tile({
      // preload: Infinity,
      ...this.layerProperties,
      visible: this.isDefault && this.initialVisibility && this.layerProperties?.visible,
    });
  }
}

class BackgroundLayerWFS extends BackgroundLayer {
  async getLayerDefinition(){
    // let typenameFirstPos = this.url.indexOf("&typename=") + 10;
    // let typenameLastPos = this.url.indexOf("&", typenameFirstPos);

    let vectorSource = new VectorSource({
      format: new GeoJSON(),
      // typename: this.url.slice(typenameFirstPos,typenameLastPos), //typename kann dem VectorSource nicht zugewiesen werden
      url: this.url.replace(/\?(.*)/,""),
      /*url: function (extent) {
        return (
          'http://test-umnmap-werner/geoserver/wfs?service=WFS&' +
          'version=1.1.0&request=GetFeature&typename=WWI:Gewaesser&' +
          'outputFormat=application/json&srsname=EPSG:3857&' +
          'bbox=' +
          extent.join(',') +
          ',EPSG:3857'
        );
      },*/
      strategy: bboxStrategy,
    });

    return new VectorLayer({
      source: vectorSource,
      ...this.layerProperties,
      visible: this.initialVisibility && this.layerProperties?.visible
    });
  }
}

class BackgroundLayerKMZ extends BackgroundLayer {
  async getLayerDefinition() {
    let dataFormat = this.url.slice(-3);
    let format = (dataFormat === "kmz")? KMZ : KML;
    let vectorSource = new VectorSource({
      url: this.url,
      format: new format({
        showPointNames: false
      }),
      ...this.properties
    });
    let vlOptions = {
      source: vectorSource,
      ...(this.layerProperties ?? this.properties.layerOpts),
      visible: this.initialVisibility && this.layerProperties?.visible,
    };
    let result = new VectorLayer(vlOptions);
    result.hasInfo = true;

    vectorSource.on('featuresloadend', (e) => {
      let source = e.target;
      
      if (source.getState() === 'ready') {
        if (!e.features || e.features.length === 0) {
          notifications.addMessage('Fehler beim Laden der KML / KMZ Datei: ' + this.label + '.'); 
        }
      } 
    });

    return result;

  }
}

/**
 *s
 * @param {object} config
 * @returns {BackgroundLayer[]}
 */
export function registerBackgroundLayers(config){
  return config.getBackgroundLayers().map((bgLayerDefinition, index) => {
    return BackgroundLayerFactory.createBackgroundLayer(bgLayerDefinition, config.getDefaultBackgroundLayerIndex() === index, index).catch(e => {
      notifications.addMessage(e)
    });
  });
}

function hasProp(obj, key) {
  return Object.prototype.hasOwnProperty.call(obj, key)
}

export function registerAdditionalLayers(config) {
  return config.getadditionalLayers().map(async (additionalLayer, index) => {
    try {
      const layer = await BackgroundLayerFactory.createBackgroundLayer(additionalLayer, true, index);
      if(!layer.layer){
        return null;
      }
      const zValue = layer.zValue;
      if (zValue != null && zValue != undefined) {
        if (zValue == zValues.BELOW_WATER)
          layer.layer.setZIndex(globals.zIndex.additionalLayersBelowWater);
        else if (zValue == zValues.ABOVE_WATER)
          layer.layer.setZIndex(globals.zIndex.additionalLayersAboveWater);
        else if (zValue == zValues.MASK) {
          layer.layer.setZIndex(globals.zIndex.mask);
          layer.layer.set("updateWhileInteracting", true);
        }
      }
      else if (Object.prototype.hasOwnProperty.call(layer.properties, "belowWater")) {
        if (layer.properties.belowWater) {
          layer.layer.setZIndex(globals.zIndex.additionalLayersBelowWater);
        }
        else {
          layer.layer.setZIndex(globals.zIndex.additionalLayersAboveWater);
        }
      }

      return layer;
    } catch (e) {
      notifications.addMessage(e)
    }
    return null;
  });
}

export function updateAdditionalLayerVisibility(additionalLayer: BackgroundLayer, modelPositions: number[]){
  if (!additionalLayer.visibleForModelsWithPosition) {
    return;
  }
  if(additionalLayer.visibleForModelsWithPosition.some(pos => modelPositions.includes(pos))){
    additionalLayer.showLayer();
  }
  else{
    additionalLayer.hideLayer();
  }
}